import {
  Heading,
  Icon,
  IconButton,
  IconType,
  TextLink,
  TextLinkProps,
} from '@assembly-js/design-system';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

export type ToastVariant =
  | 'success'
  | 'error'
  | 'warning'
  | 'neutral'
  | 'info'
  | 'progress';

export type ToastProps = {
  variant: ToastVariant;
  title?: string;
  description?: string;
  onClose?: () => void;
  actionProps?: TextLinkProps;
  progressValue?: number; // Value between 0 and 100 for progress variant
};

const iconStyles = cva('h-[13px] w-[13px] shrink-0', {
  variants: {
    variant: {
      success: 'text-success-primary',
      error: 'text-error-primary',
      info: 'text-info-primary',
      progress: 'text-black-heading',
      warning: 'text-warning-primary',
    },
  },
});

const toastStyles = cva(
  'bg-white rounded-l-xs border-0 border-l-2 border-solid shadow p-4 w-[calc(100vw-60px)] sm:w-[404px] h-fit', // On small screens, use full width minus 60px for spacing around the toast and desktop width of 404px
  {
    variants: {
      variant: {
        success: 'border-success-primary',
        error: 'border-error-primary',
        info: 'border-info-primary',
        progress: 'border-black-heading',
        neutral: 'border-black-heading',
        warning: 'border-warning-primary',
      },
    },
  },
);

const RenderIcon = ({ variant }: { variant: ToastVariant }) => {
  const getToastIcon = (): IconType => {
    switch (variant) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Failed';
      case 'info':
        return 'Info';
      case 'progress':
        return 'Upload';
      case 'warning':
        return 'Warning';
      default:
        return 'Success';
    }
  };

  if (variant === 'neutral') return null;

  return <Icon className={iconStyles({ variant })} icon={getToastIcon()} />;
};

export const Toast = ({
  variant,
  title,
  description,
  actionProps,
  onClose,
  progressValue = 0,
}: ToastProps) => {
  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);

  const isOnlyDescription = !hasTitle && hasDescription;
  const primaryText = isOnlyDescription ? description : title;

  return (
    <div className={toastStyles({ variant })}>
      <div className={'flex leading-[22px]'}>
        <span className="mr-3 shrink-0">
          <RenderIcon variant={variant} />
        </span>
        <div className="flex-1">
          {primaryText && (
            <p
              className={clsx('overflow-wrap-anywhere text-[14px] m-0', {
                'font-medium': hasTitle,
              })}
            >
              {primaryText}
            </p>
          )}
          {/* Secondary description only if both title and description exist */}
          {hasTitle && hasDescription && (
            // Ensures long or unbroken text wraps within the container to prevent overflow using overflow-wrap-anywhere
            <p className="overflow-wrap-anywhere text-[14px] m-0">
              {description}
            </p>
          )}
          {/* Progress indicator */}
          {variant === 'progress' && (
            <div className="flex items-center gap-2">
              <div className="h-[3px] w-full bg-secondary-hover rounded-full">
                <div
                  className="h-full bg-black-heading rounded-full ease-linear duration-100"
                  style={{ width: `${progressValue}%` }}
                />
              </div>
              <Heading size="xs">{progressValue}%</Heading>
            </div>
          )}
        </div>

        <div className="flex items-center h-[22px]">
          {actionProps && (
            <div className="ml-3">
              <TextLink {...actionProps} />
            </div>
          )}
          {onClose && (
            <IconButton
              icon="Close"
              variant="minimal"
              size="sm"
              onClick={onClose}
              className="[&_svg]:text-gray-small p-0 ml-2 h-fit"
            />
          )}
        </div>
      </div>
    </div>
  );
};
